import { useLayoutEffect, useRef, useState } from 'react'
import { motion, useReducedMotion, useScroll, useSpring, useTransform } from 'motion/react'

interface MotionMarqueeProps {
  label: string
  level?: 1 | 2
  className?: string
  scrollLinked?: boolean
}

export function MotionMarquee({
  label,
  level = 2,
  className = '',
  scrollLinked = true,
}: MotionMarqueeProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const [travel, setTravel] = useState(0)
  const reduceMotion = useReducedMotion()
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  })
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 82,
    damping: 24,
    mass: 0.45,
  })
  const x = useTransform(smoothProgress, [0, 0.72, 1], [travel, -travel, -travel])
  const Heading = level === 1 ? 'h1' : 'h2'

  useLayoutEffect(() => {
    const container = containerRef.current
    const track = trackRef.current
    if (!container || !track) return

    const updateTravel = () => {
      const availableSpace = (container.clientWidth - track.offsetWidth) / 2
      setTravel(Math.max(0, availableSpace - 16))
    }

    updateTravel()
    const observer = new ResizeObserver(updateTravel)
    observer.observe(container)
    observer.observe(track)
    return () => observer.disconnect()
  }, [label])

  return (
    <div ref={containerRef} className={`motion-marquee ${className}`.trim()}>
      <Heading className="sr-only">{label}</Heading>
      <motion.div
        ref={trackRef}
        className="motion-marquee-track"
        style={{ x: scrollLinked && !reduceMotion ? x : 0 }}
        aria-hidden="true"
      >
        <span className="motion-marquee-item">
          <span>{label}</span>
          <i />
        </span>
      </motion.div>
    </div>
  )
}
